<script setup lang="ts">
// Recompensas do Minha Conta V2 (ENG-362). Figma: grid de recompensas
// (18223:30087) + paginação (18270:26227). Dados reais: mesma engine da
// tela antiga (useRewards → API de gamification/rewards), reaproveitando
// as tabs/filtros antigos já com variant="v2" pronta (UserRewardsTabs/
// UserRewardsFilters) — só o card e o grid são novos, pro layout 2
// colunas deste Figma (troca o UserRewards antigo, que era lista única).
type Props = {
  modalClass?: string;
  embedded?: boolean;
};

const { modalClass = '', embedded = false } = defineProps<Props>();

// Reemitido pra quem embute esta tela num painel próprio (RewardsSheet) —
// mesmo motivo do modal legado (components/user/rewards/index.vue).
const emit = defineEmits<{ play: [] }>();

const {
  fetchRewardsFromApi,
  rewardsFromApi,
  isLoadingRewards,
  rewrdsPagination,
} = useRewards();

// mesmo kill-switch da tela antiga (components/user/rewards/index.vue):
// desativado, só existe a aba "Finalizados" — nada pra resgatar.
const { isRedeemableRewardsEnabled } = useFeatureFlagsJson();

const activeTab = ref<'available' | 'finisheds'>(
  isRedeemableRewardsEnabled.value ? 'available' : 'finisheds',
);

const activeFilter = ref<RewardType | 'all'>('all');

const isModalVisible = ref(false);
const selectedReward = ref<Reward | null>(null);

const availableRewards = computed(() => rewardsFromApi.value.available);
const finishedsRewards = computed(() => rewardsFromApi.value.finisheds);

const currentRewards = computed(() =>
  activeTab.value === 'available'
    ? availableRewards.value
    : finishedsRewards.value,
);

const currentPagination = computed(
  () => rewrdsPagination.value[activeTab.value],
);

function openModal(reward: Reward) {
  selectedReward.value = reward;
  isModalVisible.value = true;
}

function handleChangePage(page: number) {
  fetchRewardsFromApi(activeTab.value, activeFilter.value, page);
}

// redeemReward (useRewards.ts) só marca o item como "redeemed" dentro do
// próprio array `available` — não remove de lá nem busca de novo do
// servidor, então o item ficava preso em "Disponíveis" (e o badge de
// contagem não descia) até um F5. Refaz o fetch da página atual pra pegar
// a lista real do servidor, sem a recompensa que acabou de ser resgatada.
function handleRedeemed() {
  fetchRewardsFromApi(
    activeTab.value,
    activeFilter.value,
    currentPagination.value.current_page,
  );
}

onMounted(async () => {
  await fetchRewardsFromApi(activeTab.value, 'all', 1);
});
</script>

<template>
  <!-- desktop: o box ocupa a altura toda e SÓ o grid rola (tabs/filtros e
       paginação fixos) -->
  <div
    class="border-solid flex flex-col gap-[10px] md:h-full md:min-h-0"
    :class="{
      'md:rounded-[24px] md:border md:border-v2-default/40 md:p-[16px]':
        !embedded,
    }"
  >
    <UserRewardsTabs
      v-model="activeTab"
      :available-rewards="availableRewards"
      :current-type="activeFilter"
      :finisheds-rewards="finishedsRewards"
      :is-redeemable-rewards-enabled="isRedeemableRewardsEnabled"
      variant="v2"
    />

    <UserRewardsFilters
      v-model="activeFilter"
      :current-status="activeTab"
      variant="v2"
    />

    <div
      v-if="isLoadingRewards"
      class="grid grid-cols-1 content-start items-start gap-[10px]"
    >
      <div
        v-for="n in 4"
        :key="`reward-skeleton-${n}`"
        class="rewards-skeleton h-[123px] w-full rounded-[9px] bg-v2-default/40"
      />
    </div>

    <template v-else>
      <div
        v-if="currentRewards.length"
        class="user-v2-scroll grid grid-cols-1 content-start items-start gap-[10px] md:min-h-0 md:flex-1 md:overflow-y-auto md:pr-[6px]"
      >
        <UserV2RewardCard
          v-for="reward in currentRewards"
          :key="reward.id"
          :redeemable="activeTab === 'available'"
          :reward="reward"
          @redeem="openModal(reward)"
        />
      </div>

      <UserRewardsEmpty
        v-else
        :title="
          activeTab === 'available'
            ? 'Nenhum prêmio disponível'
            : 'Nenhum prêmio resgatado'
        "
      />

      <UserV2Pagination
        v-if="currentPagination.last_page > 1"
        :end="currentPagination.to"
        :last-page="currentPagination.last_page"
        :page="currentPagination.current_page"
        :start="currentPagination.from"
        :total="currentPagination.total"
        @change="handleChangePage"
      />
    </template>
  </div>

  <UserV2RewardRedeemModal
    v-model="isModalVisible"
    :overlay-class="modalClass"
    :reward="selectedReward"
    @play="emit('play')"
    @redeemed="handleRedeemed"
  />
</template>

<style scoped lang="scss">
// main.scss reseta `transition` — skeleton pulsa via keyframes
.rewards-skeleton {
  animation: rewards-pulse 1.2s ease-in-out infinite;
}

@keyframes rewards-pulse {
  0%,
  100% {
    opacity: 0.45;
  }

  50% {
    opacity: 1;
  }
}
</style>
